import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TokenRevealText.css';

const SPEED_MS = {
  fast: 90,
  medium: 140,
  slow: 200,
};

const splitWords = (text) => (text || '').trim().split(/\s+/).filter(Boolean);

/**
 * Token-wise (word-by-word) reveal.
 *
 * Usage:
 *  - <TokenRevealText text="About Us" />
 *  - <TokenRevealText parts={[{ text: 'Hello' }, { text: 'World', className: 'accent', lineBreakBefore: true }]} />
 */
const TokenRevealText = ({
  text,
  parts,
  speed = 'medium',
  startDelay = 120,
  repeatInView = false,
  className = '',
}) => {
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const items = useMemo(() => {
    const normalizedParts = Array.isArray(parts) && parts.length
      ? parts
      : [{ text: text || '', className: '' }];

    const out = [];
    normalizedParts.forEach((p, partIndex) => {
      if (p?.lineBreakBefore) out.push({ type: 'br', key: `br-${partIndex}` });

      const words = splitWords(p?.text);
      words.forEach((w, i) => {
        out.push({
          type: 'token',
          key: `t-${partIndex}-${i}-${w}`,
          text: w,
          tokenClassName: p?.className || '',
        });
        if (i < words.length - 1) out.push({ type: 'space', key: `s-${partIndex}-${i}` });
      });
    });

    return out;
  }, [parts, text]);

  const totalTokens = useMemo(
    () => items.filter((x) => x.type === 'token').length,
    [items]
  );

  const ariaLabel = useMemo(() => {
    if (Array.isArray(parts) && parts.length) return parts.map((p) => p?.text || '').join(' ').trim();
    return (text || '').trim();
  }, [parts, text]);

  useEffect(() => {
    if (prefersReducedMotion) {
      setInView(true);
      setVisibleCount(totalTokens);
      return;
    }

    const el = rootRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          setInView(true);
          if (!repeatInView) observer.disconnect();
        } else if (repeatInView) {
          setInView(false);
          setVisibleCount(0);
        }
      },
      { threshold: 0.35, rootMargin: '0px 0px -48px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [prefersReducedMotion, repeatInView, totalTokens]);

  useEffect(() => {
    if (!inView) return;
    if (prefersReducedMotion) return;

    const ms = SPEED_MS[speed] ?? SPEED_MS.medium;

    let cancelled = false;
    let t1 = null;
    let t2 = null;

    t1 = setTimeout(() => {
      if (cancelled) return;
      let i = 0;

      t2 = setInterval(() => {
        if (cancelled) return;
        i += 1;
        setVisibleCount((prev) => (prev < totalTokens ? prev + 1 : prev));
        if (i >= totalTokens) clearInterval(t2);
      }, ms);
    }, startDelay);

    return () => {
      cancelled = true;
      if (t1) clearTimeout(t1);
      if (t2) clearInterval(t2);
    };
  }, [inView, prefersReducedMotion, speed, startDelay, totalTokens]);

  let tokenIndex = 0;

  return (
    <span
      ref={rootRef}
      className={`token-reveal ${className}`}
      aria-label={ariaLabel}
    >
      {items.map((it) => {
        if (it.type === 'br') return <br key={it.key} />;
        if (it.type === 'space') return <span key={it.key}>{' '}</span>;

        tokenIndex += 1;
        const isVisible = prefersReducedMotion || tokenIndex <= visibleCount;

        return (
          <span
            key={it.key}
            className={`tr-token ${it.tokenClassName || ''} ${isVisible ? 'is-visible' : ''}`}
            aria-hidden="true"
          >
            {it.text}
          </span>
        );
      })}
    </span>
  );
};

export default TokenRevealText;
